import * as React from "react"
import { ChevronDown, Check } from "lucide-react"
import { cn } from "../../lib/utils"

export interface CustomDropdownProps {
  value: string
  onValueChange: (value: string) => void
  options: { value: string; label: string }[]
  placeholder?: string
  className?: string
  disabled?: boolean
  icon?: React.ReactNode
  iconOnly?: boolean
  hideArrow?: boolean
}

const CustomDropdown = React.forwardRef<HTMLDivElement, CustomDropdownProps>(
  ({ value, onValueChange, options, placeholder = "Select...", className, disabled = false, icon, iconOnly = false, hideArrow = false }, ref) => {
    const [open, setOpen] = React.useState(false)
    const dropdownRef = React.useRef<HTMLDivElement>(null)

    // Close dropdown on outside click
    React.useEffect(() => {
      if (!open) return
      
      function handleClickOutside(event: MouseEvent) {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setOpen(false)
        }
      }
      
      document.addEventListener("mousedown", handleClickOutside)
      return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [open])

    // Close dropdown on escape key
    React.useEffect(() => {
      function handleEscape(event: KeyboardEvent) {
        if (event.key === "Escape") {
          setOpen(false)
        }
      }
      
      document.addEventListener("keydown", handleEscape)
      return () => document.removeEventListener("keydown", handleEscape)
    }, [])

    const selectedOption = options.find(option => option.value === value)
    const displayValue = selectedOption ? selectedOption.label : placeholder

    const handleOptionClick = (optionValue: string) => {
      onValueChange(optionValue)
      setOpen(false)
    }

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        setOpen(!open)
      }
    }

    return (
      <div ref={dropdownRef} className={cn("relative", className)}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => !disabled && setOpen(!open)}
          onKeyDown={handleKeyDown}
          className={cn(
            "flex items-center justify-between rounded-md border border-slate-200 bg-white text-sm ring-offset-white placeholder:text-slate-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-950 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            iconOnly ? "h-10 w-10 p-2" : "h-10 w-full px-3 py-2",
            open && "ring-2 ring-slate-950 ring-offset-2"
          )}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={placeholder}
        >
          <div className={cn(
            "flex items-center min-w-0",
            iconOnly ? "justify-center flex-1" : "gap-2 flex-1"
          )}>
            {icon && <span className="flex-shrink-0">{icon}</span>}
            {!iconOnly && (
              <span className={cn("block truncate", !selectedOption && "text-slate-500")}>
                {displayValue}
              </span>
            )}
          </div>
          {!hideArrow && (
            <ChevronDown 
              className={cn(
                "h-4 w-4 flex-shrink-0 transition-transform duration-200",
                open && "rotate-180"
              )} 
            />
          )}
        </button>
        
        {open && (
          <div className={cn(
            "absolute z-50 mt-1 rounded-md border border-slate-200 bg-white py-1 shadow-lg",
            iconOnly ? "right-0 w-48" : "left-0 w-full"
          )}>
            {options.map((option) => {
              const isSelected = option.value === value
              return (
                <div
                  key={option.value}
                  onClick={() => handleOptionClick(option.value)}
                  className={cn(
                    "relative flex cursor-pointer select-none items-center py-2 px-3 text-sm outline-none hover:bg-slate-100 focus:bg-slate-100",
                    isSelected && "bg-slate-100 font-medium"
                  )}
                  role="option"
                  aria-selected={isSelected}
                >
                  {option.label}
                  {isSelected && (
                    <Check className="absolute right-3 h-4 w-4 text-slate-600" />
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }
)

CustomDropdown.displayName = "CustomDropdown"

export { CustomDropdown } 